import { useState } from 'react'
import { GraduationCap } from 'lucide-react'
import { useTheme } from '@/lib/theme'
import { cn } from '@/lib/cn'

/**
 * Academy brand lockup. Swaps between two image variants by theme — a
 * light-coloured logo on the dark header, a dark-coloured logo on the light
 * header — so it reads in both modes. Drop the files in `frontend/public/`:
 *   - shopview-academy-on-dark.png   (light/white logo, shown in dark mode)
 *   - shopview-academy-on-light.png  (dark logo, shown in light mode)
 * Falls back to a text lockup until the files are present.
 */
const SRC: Record<'dark' | 'light', string> = {
  dark: '/shopview-academy-on-dark.png',
  light: '/shopview-academy-on-light.png',
}

export function Logo({ variant }: { variant?: 'dark' | 'light' }) {
  const { resolved } = useTheme()
  const [failedSrc, setFailedSrc] = useState<string | null>(null)
  // `variant` forces a specific lockup regardless of theme — the always-dark
  // sidebar/login chrome needs the on-dark (light) logo even in light mode.
  const which = variant ?? resolved
  const src = SRC[which]

  if (failedSrc === src) {
    return <TextLockup onDark={which === 'dark'} />
  }

  return (
    <img
      src={src}
      alt="ShopView Academy"
      className="h-9 w-auto sm:h-10"
      onError={() => setFailedSrc(src)}
    />
  )
}

/** Text fallback (matches docs/05-brand-tokens.md §1 inline lockup). */
function TextLockup({ onDark = false }: { onDark?: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-2 font-bold',
        onDark ? 'text-ink-text' : 'text-text-primary',
      )}
      aria-label="ShopView Academy"
    >
      <GraduationCap className="h-6 w-6 text-brand" aria-hidden strokeWidth={1.75} />
      <span className="text-h4">ShopView</span>
      <span className={cn('h-5 w-px', onDark ? 'bg-ink-border' : 'bg-border')} aria-hidden />
      <span className={cn('text-h4 font-semibold', onDark ? 'text-ink-text-2' : 'text-text-secondary')}>
        Academy
      </span>
    </span>
  )
}
